import { useTheme } from "next-themes";
import { useEffect, useSyncExternalStore } from "react";

/** The browser chrome behind the status bar matches the page background in each theme. */
const THEME_COLOR = { light: "#ffffff", dark: "#130e11" } as const;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

/**
 * Registers the service worker and starts listening for the install prompt. The dev server
 * skips the worker: it would cache Vite's unhashed modules and fight hot reload.
 */
export function startPwa() {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .catch((error) => console.warn("Service worker failed", error));
    });
  }
}

export function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iOS has no install prompt; Add to Home Screen lives in Safari's share sheet. */
export function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  return ios && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

/**
 * How this browser can install the app: through the browser's own prompt, through the iOS share
 * sheet, or not at all (already installed, or the browser does not support it).
 */
export function useInstall(): { kind: "prompt" | "ios" | null; install: () => Promise<void> } {
  const prompt = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => deferredPrompt,
  );
  const kind = isStandalone() ? null : prompt ? "prompt" : isIosSafari() ? "ios" : null;
  return {
    kind,
    install: async () => {
      if (!deferredPrompt) return;
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      deferredPrompt = null;
      notify();
    },
  };
}

/** Keeps `theme-color` on the theme the app resolved, which can differ from the OS preference. */
export function ThemeColor() {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    const color = resolvedTheme === "dark" ? THEME_COLOR.dark : THEME_COLOR.light;
    for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
      meta.content = color;
    }
  }, [resolvedTheme]);
  return null;
}
